import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";

const inputClass = "rounded-none bg-void border-line text-cream mt-1.5 focus-visible:ring-gold focus-visible:border-gold";

export function MediaUploadField({ value, onChange, testId, kind = "image" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isVideo = kind === "video";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(`${isVideo ? "Video" : "Image"} uploaded`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        {value && (isVideo ? (
          <video src={value} className="h-14 w-14 object-cover border border-line" muted />
        ) : (
          <img src={value} alt="Preview" className="h-14 w-14 object-cover border border-line" />
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="rounded-none border-gold/50 text-gold hover:bg-gold hover:text-void gap-2"
          data-testid={`${testId}-upload-button`}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          {value ? `Replace ${isVideo ? "Video" : "Photo"}` : `Upload ${isVideo ? "Video" : "Photo"}`}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept={isVideo ? "video/mp4,video/quicktime" : "image/*"}
          className="hidden"
          onChange={handleFile}
          data-testid={`${testId}-file-input`}
        />
      </div>
      <Input
        placeholder={`Or paste a ${isVideo ? "video" : "image"} URL`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} text-xs`}
        data-testid={testId}
      />
    </div>
  );
}
